import { fetchProjects } from '@api/projects.js';
import Tabs from '@components/Common/Tabs.js';
import Modal from '@components/Modals/Modal.js';
import ProjectsState from '@states/Projects.js';
import UserState from '@states/User.js';
import { UIButton, UIDiv, UIText } from '@ui';

export default class MoveModal extends Modal {
  constructor({ sourceFolderId, onMove, isPersonal }) {
    super(i18next.t('modal.move.title'), {
      cancelText: i18next.t('common.cancel'),
      showCancel: true,
      showConfirm: true,
    });
    this.sourceFolderId = sourceFolderId;
    this.onMove = onMove;
    this.selectedFolderId = null;
    this.currentOrg = UserState.user?.selectedOrg;
    this.isPersonal = isPersonal;
    this.formContainer = new UIDiv().setClass('w-full space-y-6');
    this.foldersContainer = new UIDiv().setClass(
      'max-h-[50vh] mt-4 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 pr-2',
    );
    this.folderItems = new Map();
    this.projectsCache = new Map();
    this.dialogProjects = { folders: [], tasks: [], rootFolderId: null };
    this.isLoading = false;
    this.currentView = 'tabs'; // 'tabs' or 'organization'
    this.selectedOrgData = null;
    this.projectsContext = null;

    this.tabs = new Tabs(
      [
        {
          title: i18next.t('common.my_projects'),
          content: new UIDiv(),
          callback: () => this.handleTabChange('personal'),
        },
        {
          title: i18next.t('modal.move.allOrganizations'),
          content: new UIDiv(),
          callback: () => this.handleTabChange('organizations'),
        },
      ],
      'w-full',
      'underline',
      true,
    );

    this.headerContainer = new UIDiv().setClass('hidden mb-4');
    this.backButton = new UIButton('←').setClass(
      'mr-3 p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors',
    );
    this.headerText = new UIText('').setClass(
      'text-xl font-semibold text-gray-800',
    );

    const headerContent = new UIDiv().setClass('flex items-center');
    headerContent.add(this.backButton, this.headerText);
    this.headerContainer.add(headerContent);

    this.backButton.onClick(() => this.goBackToTabs());
  }

  async show() {
    const modal = await this.createModal();
    return new Promise(resolve => {
      this.resolve = resolve;
      document.body.appendChild(modal.dom);
    });
  }

  async createModal() {
    const modal = super.createModal();
    await this.render();
    return modal;
  }

  async render() {
    this.contentContainer.clear();
    this.formContainer.clear();

    this.formContainer.add(this.headerContainer);
    this.formContainer.add(this.tabs);
    this.contentContainer.add(this.formContainer);
    this.setupActionButton();

    if (this.isPersonal) {
      await this.loadDialogProjects(null);
      this.tabs.setActiveTab(0);
      this.handleTabChange('personal');
    } else {
      this.tabs.setActiveTab(1);
      this.handleTabChange('organizations');
    }
  }

  close(result) {
    super.close(result);
    if (this.resolve) {
      this.resolve();
    }
  }

  goBackToTabs() {
    this.currentView = 'tabs';
    this.selectedOrgData = null;
    this.selectedFolderId = null;
    this.actionButton.setDisabled(true);
    this.actionButton.addClass('opacity-50');

    const existingOrgContainer = this.formContainer.dom.querySelector(
      '.org-folders-container',
    );
    if (existingOrgContainer) {
      existingOrgContainer.remove();
    }

    this.headerContainer.removeClass('flex').addClass('hidden');
    this.tabs.removeClass('hidden');

    this.handleTabChange('organizations');
  }

  setLoadingState(loading) {
    this.isLoading = loading;

    if (this.actionButton) {
      this.actionButton.setDisabled(loading || !this.selectedFolderId);
      if (loading) {
        this.actionButton.addClass('opacity-50');
      } else if (this.selectedFolderId) {
        this.actionButton.removeClass('opacity-50');
      }
    }

    if (this.cancelButton) {
      this.cancelButton.setDisabled(loading);
      if (loading) {
        this.cancelButton.addClass('opacity-50 cursor-not-allowed');
      } else {
        this.cancelButton.removeClass('opacity-50 cursor-not-allowed');
      }
    }

    if (this.tabs) {
      this.tabs.setDisabled(loading);
    }

    if (this.backButton) {
      this.backButton.setDisabled(loading);
      if (loading) {
        this.backButton.addClass('opacity-50 cursor-not-allowed');
      } else {
        this.backButton.removeClass('opacity-50 cursor-not-allowed');
      }
    }
  }

  handleTabChange(tabId) {
    if (this.isLoading) return;

    if (tabId === 'personal') {
      this.renderPersonalView();
    } else {
      this.renderOrganizationsView();
    }
  }

  async loadDialogProjects(organizationId = null) {
    const cacheKey = organizationId || 'personal';

    if (this.projectsCache.has(cacheKey)) {
      this.dialogProjects = this.projectsCache.get(cacheKey);
      this.projectsContext = cacheKey;
      this.renderFoldersList();
      return;
    }

    // Check if projects are already loaded in ProjectsState for the same organization
    const currentProjectsState = ProjectsState.projects;
    const isStateLoaded =
      currentProjectsState &&
      currentProjectsState.folders &&
      currentProjectsState.folders.length > 0 &&
      !currentProjectsState.isLoading;

    // Check if the organization matches
    const stateOrgId = currentProjectsState.rootFolderOrganizationId;
    const isOrganizationMatch =
      (organizationId === null && stateOrgId === null) ||
      organizationId === stateOrgId;

    if (isStateLoaded && isOrganizationMatch) {
      // Use data from ProjectsState
      const folders = currentProjectsState.folders.map(folder => ({
        id: folder.id,
        name: folder.name,
        parentId: folder.parentId,
        isVirtual: false,
      }));

      this.dialogProjects = {
        folders,
        tasks: [],
        rootFolderId: currentProjectsState.rootFolderId,
      };

      this.projectsCache.set(cacheKey, this.dialogProjects);
      this.projectsContext = cacheKey;
      this.renderFoldersList();
      return;
    }

    try {
      this.setLoadingState(true);
      this.renderLoadingState();
      const response = await fetchProjects(organizationId);

      if (response.status === 'success') {
        const { folderInfo, subfolders } = response.response;
        const folders = subfolders.map(subfolder => ({
          id: subfolder.id,
          name: subfolder.folderName,
          parentId: subfolder.parentFolderId,
          isVirtual: false,
        }));

        this.dialogProjects = {
          folders,
          tasks: [],
          rootFolderId: folderInfo.id,
        };

        this.projectsCache.set(cacheKey, this.dialogProjects);
        this.projectsContext = cacheKey;

        this.renderFoldersList();

        // const unsortedFolder = folders.find(
        //   f => f.isDefault && f.name === 'Unsorted Tasks',
        // );
        // if (unsortedFolder) {
        //   this.handleFolderSelect(unsortedFolder.id);
        // }
      }
    } catch (error) {
      console.error('Error loading projects for dialog:', error);
      this.renderError();
    } finally {
      this.setLoadingState(false);
    }
  }

  renderPersonalView() {
    const container = new UIDiv().setClass('space-y-6 mt-2');
    container.add(this.foldersContainer);

    this.tabs.tabArray[0].content.clear();
    this.tabs.tabArray[0].content.add(container);

    if (this.projectsContext !== 'personal') {
      this.loadDialogProjects(null);
    } else {
      this.renderFoldersList();
    }
  }

  renderOrganizationsView() {
    if (this.currentView === 'organization') {
      const existingOrgContainer = this.formContainer.dom.querySelector(
        '.org-folders-container',
      );
      if (existingOrgContainer) {
        existingOrgContainer.remove();
      }

      const orgContainer = new UIDiv().setClass(
        'space-y-6 mt-2 org-folders-container',
      );
      orgContainer.add(this.foldersContainer);
      this.formContainer.add(orgContainer);

      this.renderFoldersList();
      return;
    }

    const container = new UIDiv().setClass('space-y-6');

    const orgsContainer = new UIDiv().setClass('space-y-2 mt-4');

    if (UserState.user.hasPersonalSpace) {
      this.renderOrgOption(orgsContainer, {
        id: 'personal',
        name: i18next.t('common.my_projects'),
      });
    }

    const orgs = UserState.user.orgData || [];
    orgs.forEach(org => {
      this.renderOrgOption(orgsContainer, {
        id: org.organizationId,
        name:
          org.orgSubscription?.organization?.name ||
          i18next.t('modal.move.organization'),
      });
    });

    container.add(orgsContainer);
    this.tabs.tabArray[1].content.clear();
    this.tabs.tabArray[1].content.add(container);
  }

  renderOrgOption(container, org) {
    const orgItem = new UIDiv().setClass(
      'group p-4 rounded-lg border border-gray-200 hover:border-blue-500 hover:shadow-sm ' +
        'transition-all duration-200 ease-in-out cursor-pointer bg-white',
    );

    const nameContainer = new UIDiv().setClass(
      'flex items-center justify-between',
    );
    const name = new UIText(org.name).setClass(
      'font-medium text-gray-700 group-hover:text-blue-600',
    );
    const arrow = new UIText('→').setClass(
      'text-gray-400 group-hover:text-blue-500 transform group-hover:translate-x-1 ' +
        'transition-all duration-200 ease-in-out',
    );

    nameContainer.add(name, arrow);
    orgItem.add(nameContainer);
    orgItem.onClick(async () => {
      if (this.isLoading) return;

      try {
        this.setLoadingState(true);
        orgItem.addClass('opacity-50 pointer-events-none');

        const orgId = org.id === 'personal' ? null : org.id;
        const cacheKey = orgId || 'personal';

        if (this.projectsCache.has(cacheKey)) {
          this.dialogProjects = this.projectsCache.get(cacheKey);
          this.projectsContext = cacheKey;
        } else {
          // Check if projects are already loaded in ProjectsState for this organization
          const currentProjectsState = ProjectsState.projects;
          const isStateLoaded =
            currentProjectsState &&
            currentProjectsState.folders &&
            currentProjectsState.folders.length > 0 &&
            !currentProjectsState.isLoading;

          // Check if the organization matches
          const stateOrgId = currentProjectsState.rootFolderOrganizationId;
          const isOrganizationMatch =
            (orgId === null && stateOrgId === null) || orgId === stateOrgId;

          if (isStateLoaded && isOrganizationMatch) {
            // Use data from ProjectsState
            const folders = currentProjectsState.folders.map(folder => ({
              id: folder.id,
              name: folder.name,
              parentId: folder.parentId,
              isVirtual: false,
            }));

            this.dialogProjects = {
              folders,
              tasks: [],
              rootFolderId: currentProjectsState.rootFolderId,
            };

            // Cache the data
            this.projectsCache.set(cacheKey, this.dialogProjects);
            this.projectsContext = cacheKey;
          } else {
            // Fetch from API if not available in state
            const response = await fetchProjects(orgId);
            if (response.status !== 'success') {
              throw new Error('Failed to fetch projects');
            }

            const { folderInfo, subfolders } = response.response;
            const folders = subfolders.map(subfolder => ({
              id: subfolder.id,
              name: subfolder.folderName,
              parentId: subfolder.parentFolderId,
              isVirtual: false,
            }));

            this.dialogProjects = {
              folders,
              tasks: [],
              rootFolderId: folderInfo.id,
            };

            // Cache the fetched data
            this.projectsCache.set(cacheKey, this.dialogProjects);
            this.projectsContext = cacheKey;
          }
        }

        this.currentView = 'organization';
        this.selectedOrgData = org;
        this.selectedFolderId = null;
        this.actionButton.setDisabled(true);
        this.actionButton.addClass('opacity-50');

        this.headerText.setValue(org.name);
        this.headerContainer.removeClass('hidden').addClass('flex');
        this.tabs.addClass('hidden');

        this.renderOrganizationsView();
      } catch (error) {
        console.error('Error fetching organization projects:', error);
        orgItem.removeClass('opacity-50 pointer-events-none');

        const existingErrors = container.dom.querySelectorAll('.bg-red-50');
        existingErrors.forEach(error => error.remove());

        const errorContainer = new UIDiv().setClass(
          'p-4 text-red-500 text-center bg-red-50 rounded-lg mt-2',
        );
        errorContainer.setInnerText(
          i18next.t('modal.move.permission_or_subscription_missing'),
        );
        container.add(errorContainer);

        setTimeout(() => {
          if (errorContainer.dom && errorContainer.dom.parentNode) {
            errorContainer.dom.remove();
          }
        }, 5000);
      } finally {
        this.setLoadingState(false);
        orgItem.removeClass('opacity-50 pointer-events-none');
      }
    });

    container.add(orgItem);
  }

  renderLoadingState() {
    this.foldersContainer.clear();
    this.foldersContainer.add(
      new UIText(i18next.t('modal.move.loading')).setClass(
        'text-gray-500 p-4 text-center italic',
      ),
    );
  }

  renderError() {
    this.foldersContainer.clear();
    this.foldersContainer.add(
      new UIText(i18next.t('modal.move.error')).setClass(
        'text-red-500 p-4 text-center',
      ),
    );
  }

  renderFoldersList() {
    this.foldersContainer.clear();
    this.folderItems.clear();

    const folders = this.dialogProjects.folders.filter(
      folder => folder.id !== this.sourceFolderId && !folder.isVirtual,
    );

    if (folders.length === 0) {
      this.foldersContainer.add(
        new UIText(i18next.t('modal.move.no_projects')).setClass(
          'text-gray-500 p-4',
        ),
      );
      return;
    }

    folders.forEach(folder => this.renderFolderItem(folder));

    // if (!this.selectedFolderId) {
    //   const unsortedFolder = folders.find(
    //     f => f.isDefault && f.name === 'Unsorted Tasks',
    //   );
    //   if (unsortedFolder) {
    //     this.handleFolderSelect(unsortedFolder.id);
    //     return;
    //   }
    // }

    if (this.selectedFolderId && this.folderItems.has(this.selectedFolderId)) {
      const selectedItem = this.folderItems.get(this.selectedFolderId);
      selectedItem.radio
        .removeClass('border-gray-300')
        .addClass('bg-blue-500 border-blue-500');
      selectedItem.element.addClass('bg-blue-50');
      this.actionButton.setDisabled(false);
      this.actionButton.removeClass('opacity-50');
    }
  }

  renderFolderItem(folder) {
    const folderItem = new UIDiv().setClass(
      'group p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-all duration-200 ' +
        'ease-in-out flex items-center space-x-3',
    );

    const radio = new UIDiv().setClass(
      'w-5 h-5 rounded-full border-2 border-gray-300 flex-shrink-0 transition-colors ' +
        'duration-200 ease-in-out group-hover:border-blue-500',
    );

    const name = new UIText(folder.name).setClass(
      'flex-grow font-medium text-gray-700 group-hover:text-gray-900',
    );

    folderItem.add(radio, name);
    folderItem.onClick(() => this.handleFolderSelect(folder.id));
    this.folderItems.set(folder.id, { element: folderItem, radio });
    this.foldersContainer.add(folderItem);
  }

  handleFolderSelect(folderId) {
    if (this.selectedFolderId && this.folderItems.has(this.selectedFolderId)) {
      const prevItem = this.folderItems.get(this.selectedFolderId);
      prevItem.radio
        .removeClass('bg-blue-500 border-blue-500')
        .addClass('border-gray-300');
      prevItem.element.removeClass('bg-blue-50');
    }

    if (this.selectedFolderId === folderId) {
      this.selectedFolderId = null;
      this.actionButton.setDisabled(true);
      this.actionButton.addClass('opacity-50');
      return;
    }

    const newItem = this.folderItems.get(folderId);
    if (newItem) {
      newItem.radio
        .removeClass('border-gray-300')
        .addClass('bg-blue-500 border-blue-500');
      newItem.element.addClass('bg-blue-50');
      this.selectedFolderId = folderId;
      this.actionButton.setDisabled(false);
      this.actionButton.removeClass('opacity-50');
    }
  }

  setupActionButton() {
    this.actionButton
      .setInnerText(i18next.t('common.move'))
      .setClass(
        'px-6 py-2.5 rounded-lg bg-blue-600 text-white font-medium ' +
          'hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 ' +
          'focus:ring-offset-2 transition-all duration-200 ease-in-out disabled:opacity-50 ' +
          'disabled:cursor-not-allowed',
      )
      .setDisabled(true);
  }

  async onAction() {
    if (!this.selectedFolderId || this.isLoading) return;

    return new Promise(async (resolve, reject) => {
      try {
        this.setLoadingState(true);
        this.actionButton.setInnerText(i18next.t('modal.move.moving'));

        if (this.onMove) {
          await this.onMove(this.selectedFolderId);
          this.close();
          resolve(true);
        } else {
          resolve(false);
        }
      } catch (error) {
        console.error('Error moving task:', error);
        this.actionButton.setInnerText(i18next.t('common.move'));
        reject(error);
      } finally {
        this.setLoadingState(false);
      }
    });
  }
}
